import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BRAND, ASSETS } from "../data/content";
import { scrollToId } from "../hooks/useLenis";

function Particles() {
  const dots = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[6]" aria-hidden>
      {dots.map((_, i) => {
        const left = (i * 41) % 100;
        const size = 1 + (i % 3);
        const dur = 10 + (i % 7) * 2;
        const delay = (i % 10) * 0.8;
        return (
          <span
            key={i}
            className="absolute rounded-full bg-white/50"
            style={{ left: `${left}%`, bottom: "-10px", width: size, height: size, animation: `drift ${dur}s linear ${delay}s infinite` }}
          />
        );
      })}
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });

  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * -24);
    my.set(((e.clientY - r.top) / r.height - 0.5) * -24);
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMouse}
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
      data-testid="section-hero"
    >
      {/* full-bleed portrait */}
      <motion.div className="absolute inset-0 z-0" style={{ y: yImg, scale: scaleImg }}>
        <motion.img
          src={ASSETS.portraitSeated}
          alt={`${BRAND.name} — ${BRAND.phrase}`}
          className="w-full h-full object-cover object-center md:object-[center_20%] grayscale-[0.12] contrast-[1.05]"
          style={{ x: sx, y: sy }}
          initial={{ opacity: 0, filter: "blur(24px)", scale: 1.12 }}
          animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>

      {/* cinematic gradients / vignette */}
      <div className="absolute inset-0 z-[3] pointer-events-none"
        style={{ background: "linear-gradient(to bottom, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.15) 30%, rgba(5,5,5,0.35) 65%, var(--void) 100%)" }} />
      <div className="absolute inset-0 z-[3] pointer-events-none"
        style={{ background: "radial-gradient(120% 90% at 50% 40%, transparent 45%, rgba(5,5,5,0.7) 100%)" }} />

      <Particles />

      {/* chrome wordmark overlay */}
      <motion.div
        style={{ y: yText, opacity: textOpacity }}
        className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6"
      >
        <motion.p
          className="track-label text-white/60 mb-5 md:mb-7"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1 }}
        >
          {BRAND.tagline}
        </motion.p>

        <motion.h1
          className="font-display chrome-text text-[15vw] md:text-[11vw] leading-[0.9] tracking-[0.02em] drop-shadow-[0_4px_40px_rgba(0,0,0,0.6)]"
          initial={{ opacity: 0, letterSpacing: "0.28em", y: 30 }}
          animate={{ opacity: 1, letterSpacing: "0.02em", y: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        >
          {BRAND.name}
        </motion.h1>

        <motion.div
          className="flex items-center gap-4 mt-4 md:mt-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }}
        >
          <span className="h-px w-10 bg-white/40" />
          <p className="font-display italic steel-text text-xl md:text-3xl">{BRAND.phrase}</p>
          <span className="h-px w-10 bg-white/40" />
        </motion.div>
      </motion.div>

      {/* enter cue */}
      <motion.button
        onClick={() => scrollToId("about")}
        data-testid="hero-enter"
        data-cursor
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 group"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
      >
        <span className="track-label text-white/60 group-hover:text-white transition-colors">ENTER THE WORLD</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.4} className="text-white/70" />
        </motion.span>
      </motion.button>
    </section>
  );
}
